import { http } from "@/shared/lib/api/http"
import { useMembersStore } from "@/features/members/services/members-store"
import {
  ACTIVITY_LOG_ACTION_TYPES,
  ACTIVITY_LOG_DOMAINS,
  ACTIVITY_LOG_ENTITY_TYPES,
  type ActivityActor,
  type ActivityLogChange,
  type ActivityLogDomain,
  type ActivityLogFacets,
  type ActivityEvent,
  type ActivityFilters,
  type ActivityListResult,
  type ActivityLogActionType,
  type ActivityLogEntityType,
  type ActivityLogItem,
  type GetActivityLogsQuery,
  type GetActivityLogsResponse,
} from "@/features/activity/model/activity-types"

type RecordActivityEventInput = Omit<ActivityEvent, "id"> & {
  id?: string
}

type ListActivityLogsInput = {
  workspaceSlug: string
  branchSlug: string
  query?: GetActivityLogsQuery
}

function createActivityId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`
}

function resolveActorFromEmail(email?: string): ActivityActor {
  if (!email) {
    return { name: "System" }
  }

  const member = useMembersStore
    .getState()
    .listMembers()
    .find((item) => (item.email ?? "").toLowerCase() === email.toLowerCase())

  if (!member) {
    return { name: email, email }
  }

  return {
    id: member.id,
    name: member.name,
    email: member.email ?? undefined,
    role: member.role,
  }
}

function normalizeString(value: unknown) {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function normalizeNullableString(value: unknown) {
  if (value === null || value === undefined) return null
  return normalizeString(value) ?? null
}

function normalizeEntityType(value: unknown): ActivityLogEntityType | undefined {
  const normalized = normalizeString(value)
  if (!normalized) return undefined
  return ACTIVITY_LOG_ENTITY_TYPES.includes(normalized as ActivityLogEntityType)
    ? (normalized as ActivityLogEntityType)
    : undefined
}

function normalizeActionType(value: unknown): ActivityLogActionType | undefined {
  const normalized = normalizeString(value)
  if (!normalized) return undefined
  return ACTIVITY_LOG_ACTION_TYPES.includes(normalized as ActivityLogActionType)
    ? (normalized as ActivityLogActionType)
    : undefined
}

function normalizeMessageParams(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function normalizeDomain(value: unknown): ActivityLogDomain | undefined {
  const normalized = normalizeString(value)
  if (!normalized) return undefined
  return ACTIVITY_LOG_DOMAINS.includes(normalized as ActivityLogDomain) ? (normalized as ActivityLogDomain) : undefined
}

function normalizeChangeValue(value: unknown): ActivityLogChange["before"] {
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value
  return null
}

function normalizeChanges(value: unknown): ActivityLogChange[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return []
    const record = entry as Record<string, unknown>
    const field = normalizeString(record.field)
    if (!field) return []
    return [
      {
        field,
        before: normalizeChangeValue(record.before),
        after: normalizeChangeValue(record.after),
        redacted: record.redacted === true,
      },
    ]
  })
}

function normalizeActivityLogItem(value: unknown): ActivityLogItem | null {
  if (!value || typeof value !== "object") return null

  const item = value as Record<string, unknown>
  const id = normalizeString(item.id)
  const workspaceId = normalizeString(item.workspaceId)
  const entityId = normalizeString(item.entityId)
  const actionType = normalizeActionType(item.actionType)
  const entityType = normalizeEntityType(item.entityType)
  const messageKey = normalizeString(item.messageKey)
  const createdAt = normalizeString(item.createdAt)

  if (
    !id ||
    !workspaceId ||
    !entityId ||
    !actionType ||
    !entityType ||
    !messageKey ||
    !createdAt
  ) {
    return null
  }

  return {
    id,
    workspaceId,
    branchId: normalizeNullableString(item.branchId),
    actorUserId: normalizeNullableString(item.actorUserId),
    actorDisplayName: normalizeNullableString(item.actorDisplayName),
    actorAvatarUrl: normalizeNullableString(item.actorAvatarUrl),
    actionType,
    entityType,
    entityId,
    entityLabel: normalizeNullableString(item.entityLabel),
    messageKey,
    messageParams: normalizeMessageParams(item.messageParams),
    summary: normalizeNullableString(item.summary),
    changes: normalizeChanges(item.changes),
    createdAt,
    canPreview: Boolean(item.canPreview),
  }
}

function normalizeActivityLogsResponse(payload: unknown): GetActivityLogsResponse {
  if (!payload || typeof payload !== "object") {
    return { items: [], total: 0, page: 1, limit: 20 }
  }

  const record = payload as Record<string, unknown>
  const rawItems = Array.isArray(record.items) ? record.items : []
  const items = rawItems
    .map((entry) => normalizeActivityLogItem(entry))
    .filter((entry): entry is ActivityLogItem => Boolean(entry))

  return {
    items,
    total: typeof record.total === "number" ? record.total : items.length,
    page: typeof record.page === "number" ? record.page : 1,
    limit: typeof record.limit === "number" ? record.limit : Math.max(items.length, 1),
  }
}

function resolveLimit(limit: unknown) {
  if (typeof limit !== "number" || !Number.isFinite(limit)) return 20
  return Math.min(100, Math.max(1, Math.trunc(limit)))
}

async function listActivityLogs({
  workspaceSlug,
  branchSlug,
  query,
}: ListActivityLogsInput): Promise<GetActivityLogsResponse> {
  const params: Record<string, string | number> = {}

  const branch = normalizeString(query?.branch)
  const actorUserId = normalizeString(query?.actorUserId)
  const entityType = normalizeEntityType(query?.entityType)
  const actionType = normalizeActionType(query?.actionType)
  const domain = normalizeDomain(query?.domain)
  const entityId = normalizeString(query?.entityId)
  const from = normalizeString(query?.from)
  const to = normalizeString(query?.to)
  const q = normalizeString(query?.q)
  const limit = resolveLimit(query?.limit)
  const page = resolvePage(query?.page)
  const sortBy = normalizeString(query?.sortBy)
  const sortOrder = normalizeSortOrder(query?.sortOrder)
  const filters = normalizeString(query?.filters)

  if (branch) params.branch = branch
  if (actorUserId) params.actorUserId = actorUserId
  if (entityType) params.entityType = entityType
  if (actionType) params.actionType = actionType
  if (domain) params.domain = domain
  if (entityId) params.entityId = entityId
  if (from) params.from = from
  if (to) params.to = to
  if (q) params.q = q
  if (filters) params.filters = filters
  if (sortBy) params.sortBy = sortBy
  if (sortOrder) params.sortOrder = sortOrder
  params.page = page
  params.limit = limit

  const response = await http.get<GetActivityLogsResponse>("/activity-logs", {
    params,
    headers: {
      "x-workspace-slug": workspaceSlug,
      "x-branch-slug": branchSlug,
    },
  })

  return normalizeActivityLogsResponse(response.data)
}

function normalizeActivityLogFacets(payload: unknown): ActivityLogFacets {
  const record = payload && typeof payload === "object" ? (payload as Record<string, unknown>) : {}
  const domains = Array.isArray(record.domains) ? record.domains : []
  const actors = Array.isArray(record.actors) ? record.actors : []
  return {
    total: typeof record.total === "number" ? record.total : 0,
    domains: domains.flatMap((entry) => {
      const item = entry as Record<string, unknown>
      const domain = normalizeDomain(item?.domain)
      return domain ? [{ domain, count: typeof item.count === "number" ? item.count : 0 }] : []
    }),
    actors: actors.flatMap((entry) => {
      const item = entry as Record<string, unknown>
      const actorUserId = normalizeString(item?.actorUserId)
      if (!actorUserId) return []
      return [
        {
          actorUserId,
          actorDisplayName: normalizeNullableString(item.actorDisplayName),
          actorAvatarUrl: normalizeNullableString(item.actorAvatarUrl),
          count: typeof item.count === "number" ? item.count : 0,
        },
      ]
    }),
  }
}

/** Domain counts and the people for the audit log filters, under the list's own authorization and filters. */
async function getActivityLogFacets({
  workspaceSlug,
  branchSlug,
  query,
}: ListActivityLogsInput): Promise<ActivityLogFacets> {
  const params: Record<string, string> = {}
  const q = normalizeString(query?.q)
  const filters = normalizeString(query?.filters)
  const domain = normalizeDomain(query?.domain)
  const entityId = normalizeString(query?.entityId)
  const actorUserId = normalizeString(query?.actorUserId)
  const from = normalizeString(query?.from)
  const to = normalizeString(query?.to)
  if (q) params.q = q
  if (filters) params.filters = filters
  if (domain) params.domain = domain
  if (entityId) params.entityId = entityId
  if (actorUserId) params.actorUserId = actorUserId
  if (from) params.from = from
  if (to) params.to = to

  const response = await http.get<ActivityLogFacets>("/activity-logs/facets", {
    params,
    headers: {
      "x-workspace-slug": workspaceSlug,
      "x-branch-slug": branchSlug,
    },
  })
  return normalizeActivityLogFacets(response.data)
}

// Compatibility no-op while backend is source of truth for audit logs.
async function recordActivityEvent(input: RecordActivityEventInput) {
  return {
    ...input,
    id: input.id ?? createActivityId("act"),
  }
}

async function listActivityEvents(filters: ActivityFilters): Promise<ActivityListResult> {
  return {
    items: [],
    total: 0,
    pageIndex: filters.pageIndex,
    pageSize: filters.pageSize,
  }
}

async function getActivityEventById(id: string) {
  void id
  return undefined
}

export const activityApi = {
  resolveActorFromEmail,
  recordActivityEvent,
  listActivityEvents,
  getActivityEventById,
  listActivityLogs,
  getActivityLogFacets,
}

function resolvePage(page: unknown) {
  if (typeof page !== "number" || !Number.isFinite(page)) return 1
  return Math.max(1, Math.trunc(page))
}

function normalizeSortOrder(value: unknown): "asc" | "desc" | undefined {
  if (value === "asc" || value === "desc") return value
  return undefined
}
